
import { defineComponent, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import AppLabel from '@/components/AppLabel.vue';
import {
  ICase, IFolder, IMedia, IPhoto, ISharedLable,
} from '@/types/interfaces';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import { NAVIGATION_PAGES, PHOTO_VIEW_TYPE } from '@/types/enums';
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';
import { contactsToLables, everyoneToLables, groupsToLables } from '@/helpers/labelsFormatter';

export default defineComponent({
  components: { AppLabel },
  props: {
    shareToAll: { type: Boolean, default: false },
    showDelete: { type: Boolean, default: false },
    item: { type: Object as () => IMedia },
  },
  emits: ['on-visit-chat'],
  setup(props, context) {
    const router = useRouter();
    const route = useRoute();
    const driveStore = useDriveStore();
    const spacesStore = useSpacesStore();

    const currentPage = computed(() => route.meta.navigationPage);

    const currentItem = computed<IMedia | IFolder | ICase | null>(() => {
      if (props?.item) {
        return props.item;
      }
      if (driveStore.appPhotoViewMedia?.share) {
        return driveStore.appPhotoViewMedia;
      }
      if (driveStore.fullscreenPhoto?.share) {
        return driveStore.fullscreenPhoto;
      }
      if (driveStore.fullscreenFile?.share) {
        return driveStore.fullscreenFile;
      }
      if (driveStore.currentFolder?.share) {
        return driveStore.currentFolder;
      }
      if (driveStore.currentCase?.id && driveStore.currentCase?.share
          && (currentPage.value === NAVIGATION_PAGES.CASE_VIEW
              || currentPage.value === NAVIGATION_PAGES.MESSAGES_ID
              || currentPage.value === NAVIGATION_PAGES.MESSAGES_GROUP_CHAT_ID)) {
        return driveStore.currentCase;
      }
      return null;
    });

    const sharedTo = computed(() => {
      if (currentItem.value?.share) {
        if (currentItem.value.share.everyone) {
          return everyoneToLables(spacesStore.myContacts, spacesStore.myGroups);
        }
        return [
          ...contactsToLables(spacesStore.myContacts, currentItem.value.share.sharedAccounts.map((shareId) => shareId.internalId)),
          ...groupsToLables(spacesStore.myGroups, currentItem.value.share.sharedGroups),
        ];
      }
      return [];
    });

    function resolvePhoto(item: { name: string, photo: IPhoto, isGroup: boolean }): string {
      if (item.photo?.url) {
        return lowResUrlByViewTypeByUrl(item.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    }

    function goToChat(id: string, isGroup: boolean) {
      if (props.showDelete) return;
      context.emit('on-visit-chat');
      if (isGroup) {
        router.push(`/messages/group/${id}`);
      } else {
        router.push(`/messages/${id}`);
      }
    }

    function removeSharingFlag() {
      driveStore.cases.data = driveStore.cases.data.map((cas) => {
        if (cas.id === currentItem.value?.id) {
          return { ...cas, shared: false };
        }
        return cas;
      });
      driveStore.photos.data = driveStore.photos.data.map((photo) => {
        if (photo.id === currentItem.value?.id) {
          return { ...photo, shared: false };
        }
        return photo;
      });
      driveStore.mixedData.data = driveStore.mixedData.data.map((mix) => {
        if (mix.id === currentItem.value?.id) {
          if (mix.case) {
            // eslint-disable-next-line no-param-reassign
            mix.case.share = null;
          } else if (mix.file) {
            // eslint-disable-next-line no-param-reassign
            mix.file.share = null;
          } else if (mix.folder) {
            // eslint-disable-next-line no-param-reassign
            mix.folder.share = null;
          }
        }
        return mix;
      });
      if (driveStore.currentFolder?.mixedData) {
        driveStore.currentFolder.mixedData = driveStore.currentFolder.mixedData.map((mix) => {
          if (mix.id === currentItem.value?.id) {
            if (mix.case) {
            // eslint-disable-next-line no-param-reassign
              mix.case.share = null;
            } else if (mix.file) {
            // eslint-disable-next-line no-param-reassign
              mix.file.share = null;
            } else if (mix.folder) {
            // eslint-disable-next-line no-param-reassign
              mix.folder.share = null;
            }
          }
          return mix;
        });
      }
      if (driveStore.currentCase) {
        driveStore.currentCase.photos = driveStore.currentCase.photos.map((photo) => {
          if (photo.id === currentItem.value?.id) {
            return { ...photo, shared: false };
          }
          return photo;
        });
        driveStore.currentCase.files = driveStore.currentCase.files.map((file) => {
          if (file.id === currentItem.value?.id) {
            return { ...file, shared: false };
          }
          return file;
        });
      }
    }

    function removeShare(label: ISharedLable) {
      driveStore.removeSharingInfo(currentItem.value!, label);
    }

    return {
      currentItem,
      sharedTo,
      resolvePhoto,
      goToChat,
      removeShare,
    };
  },
});
